import { useState } from 'react';
import { Input, Tabs, Typography } from 'antd';
import { WarningOutlined, CheckOutlined } from '@ant-design/icons';
import { LANGUAGE_MAP } from '../../i18n/languageOptions';
import type { LanguageCode } from '../../types/i18n';

interface TranslatedInputProps {
  value: Partial<Record<LanguageCode, string>>;
  onChange: (value: Partial<Record<LanguageCode, string>>) => void;
  supportedLanguages: LanguageCode[];
  defaultLanguage: LanguageCode;
  required?: boolean;
  multiline?: boolean;
  error?: Partial<Record<LanguageCode, string>>;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  /**
   * 'tabs' (default) — language tabs with single input.
   * 'stacked' — all language inputs visible at once, one row per language.
   */
  layout?: 'tabs' | 'stacked';
}

export function TranslatedInput({
  value,
  onChange,
  supportedLanguages,
  defaultLanguage,
  required = false,
  multiline = false,
  error,
  placeholder,
  label,
  disabled = false,
  layout = 'tabs',
}: TranslatedInputProps) {
  const initialTab: LanguageCode =
    supportedLanguages.includes(defaultLanguage)
      ? defaultLanguage
      : (supportedLanguages[0] ?? 'uz');

  const [activeTab, setActiveTab] = useState<LanguageCode>(initialTab);

  const handleChange = (lang: LanguageCode, text: string) => {
    onChange({ ...value, [lang]: text });
  };

  // Single language → plain input (no tabs)
  if (supportedLanguages.length <= 1) {
    const lang: LanguageCode = supportedLanguages[0] ?? 'uz';
    const currentValue = value[lang] ?? '';
    const currentError = error?.[lang];

    return (
      <div>
        {label !== undefined && (
          <Typography.Text
            style={{
              display: 'block',
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--bl-text-muted, #6b7280)',
              marginBottom: 6,
            }}
          >
            {label}
            {required && (
              <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>
            )}
          </Typography.Text>
        )}
        {multiline ? (
          <Input.TextArea
            value={currentValue}
            onChange={(e) => handleChange(lang, e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            rows={3}
            style={{
              borderRadius: 12,
              borderColor: currentError !== undefined ? '#ef4444' : undefined,
            }}
          />
        ) : (
          <Input
            value={currentValue}
            onChange={(e) => handleChange(lang, e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            style={{
              borderRadius: 12,
              height: 40,
              borderColor: currentError !== undefined ? '#ef4444' : undefined,
            }}
          />
        )}
        {currentError !== undefined && (
          <Typography.Text
            style={{
              fontSize: 12,
              color: '#ef4444',
              marginTop: 4,
              display: 'block',
            }}
          >
            {currentError}
          </Typography.Text>
        )}
      </div>
    );
  }

  // Multi-language, stacked layout → one row per language, all visible
  if (layout === 'stacked') {
    return (
      <div>
        {label !== undefined && (
          <Typography.Text
            style={{
              display: 'block',
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--bl-text-muted, #6b7280)',
              marginBottom: 8,
            }}
          >
            {label}
            {required && (
              <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>
            )}
          </Typography.Text>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {supportedLanguages.map((lang) => {
            const opt = LANGUAGE_MAP[lang];
            const langValue = value[lang] ?? '';
            const isEmpty = langValue.trim().length === 0;
            const isDefault = lang === defaultLanguage;
            const hasError = error?.[lang] !== undefined;
            const langError = error?.[lang];
            const inputPlaceholder =
              placeholder ?? (isDefault && required ? 'Majburiy' : 'Ixtiyoriy');

            // Status indicator: filled (green), empty (gray), error (red)
            const statusColor = hasError
              ? '#ef4444'
              : !isEmpty
                ? '#22c55e'
                : '#cbd5e1';
            const statusBg = hasError
              ? 'rgba(239,68,68,0.10)'
              : !isEmpty
                ? 'rgba(34,197,94,0.12)'
                : 'rgba(148,163,184,0.16)';

            return (
              <div key={lang}>
                {/* Row header: flag + language + ASOSIY badge + status dot */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 6,
                  }}
                >
                  <span
                    style={{
                      fontSize: 17,
                      lineHeight: 1,
                      flexShrink: 0,
                    }}
                  >
                    {opt?.flag ?? lang}
                  </span>
                  <Typography.Text
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: 'var(--bl-text-strong, #0f172a)',
                      letterSpacing: -0.1,
                    }}
                  >
                    {opt?.label ?? lang}
                  </Typography.Text>
                  {isDefault && (
                    <span
                      style={{
                        fontSize: 9,
                        fontWeight: 800,
                        padding: '2px 6px',
                        borderRadius: 999,
                        background: 'rgba(51,144,236,0.10)',
                        color: '#1d6fd5',
                        border: '1px solid rgba(51,144,236,0.20)',
                        textTransform: 'uppercase',
                        letterSpacing: 0.3,
                        lineHeight: 1,
                      }}
                    >
                      Asosiy
                    </span>
                  )}
                  {/* Status indicator */}
                  <span
                    style={{
                      marginLeft: 'auto',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      background: statusBg,
                      color: statusColor,
                      fontSize: 10,
                      flexShrink: 0,
                    }}
                    aria-hidden
                  >
                    {hasError ? (
                      <WarningOutlined style={{ fontSize: 10 }} />
                    ) : !isEmpty ? (
                      <CheckOutlined style={{ fontSize: 10 }} />
                    ) : (
                      <span
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          background: statusColor,
                        }}
                      />
                    )}
                  </span>
                </div>

                {multiline ? (
                  <Input.TextArea
                    value={langValue}
                    onChange={(e) => handleChange(lang, e.target.value)}
                    placeholder={inputPlaceholder}
                    disabled={disabled}
                    rows={3}
                    style={{
                      borderRadius: 10,
                      borderColor: hasError ? '#ef4444' : undefined,
                      background: 'var(--bl-surface-soft, #f7f8fa)',
                    }}
                  />
                ) : (
                  <Input
                    value={langValue}
                    onChange={(e) => handleChange(lang, e.target.value)}
                    placeholder={inputPlaceholder}
                    disabled={disabled}
                    style={{
                      borderRadius: 10,
                      height: 40,
                      borderColor: hasError ? '#ef4444' : undefined,
                      background: 'var(--bl-surface-soft, #f7f8fa)',
                    }}
                  />
                )}
                {langError !== undefined && (
                  <Typography.Text
                    style={{
                      fontSize: 12,
                      color: '#ef4444',
                      marginTop: 4,
                      display: 'block',
                    }}
                  >
                    {langError}
                  </Typography.Text>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Multi-language → tabbed input
  const tabItems = supportedLanguages.map((lang) => {
    const opt = LANGUAGE_MAP[lang];
    const langValue = value[lang] ?? '';
    const isEmpty = langValue.trim().length === 0;
    const isDefault = lang === defaultLanguage;
    const hasError = error?.[lang] !== undefined;

    const tabLabel = (
      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <span>{opt?.flag ?? lang}</span>
        <span>{opt?.label ?? lang}</span>
        {isDefault && required && (
          <span style={{ color: '#d97706', fontSize: 11 }}>*</span>
        )}
        {hasError ? (
          <WarningOutlined style={{ color: '#ef4444', fontSize: 11 }} />
        ) : isEmpty ? (
          <WarningOutlined style={{ color: '#d97706', fontSize: 11 }} />
        ) : (
          <CheckOutlined style={{ color: '#22c55e', fontSize: 11 }} />
        )}
      </span>
    );

    const langError = error?.[lang];
    const inputPlaceholder =
      placeholder ?? (isDefault && required ? 'Majburiy' : 'Ixtiyoriy');

    return {
      key: lang,
      label: tabLabel,
      children: (
        <div>
          {multiline ? (
            <Input.TextArea
              value={langValue}
              onChange={(e) => handleChange(lang, e.target.value)}
              placeholder={inputPlaceholder}
              disabled={disabled}
              rows={3}
              style={{
                borderRadius: 12,
                borderColor: hasError ? '#ef4444' : undefined,
              }}
            />
          ) : (
            <Input
              value={langValue}
              onChange={(e) => handleChange(lang, e.target.value)}
              placeholder={inputPlaceholder}
              disabled={disabled}
              style={{
                borderRadius: 12,
                height: 40,
                borderColor: hasError ? '#ef4444' : undefined,
              }}
            />
          )}
          {langError !== undefined && (
            <Typography.Text
              style={{
                fontSize: 12,
                color: '#ef4444',
                marginTop: 4,
                display: 'block',
              }}
            >
              {langError}
            </Typography.Text>
          )}
        </div>
      ),
    };
  });

  return (
    <div>
      {label !== undefined && (
        <Typography.Text
          style={{
            display: 'block',
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--bl-text-muted, #6b7280)',
            marginBottom: 6,
          }}
        >
          {label}
          {required && (
            <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>
          )}
        </Typography.Text>
      )}
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as LanguageCode)}
        items={tabItems}
        size="small"
        style={{ marginBottom: 0 }}
      />
    </div>
  );
}
